import { IconCompass, IconUsersGroup } from '@tabler/icons-react-native';
import { Text } from 'app/design/primitives/text';
import { createTheme } from 'app/shopify';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Pressable,
  ScrollView,
  StyleSheet,
  View,
  type NativeScrollEvent,
  type NativeSyntheticEvent,
} from 'react-native';
import Animated, {
  useAnimatedScrollHandler,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  type SharedValue,
} from 'react-native-reanimated';

const theme = createTheme();
const INDICATOR_WIDTH = 64;

function LandingFeedTabIndicator({
  width,
  tabCount,
  progress,
}: {
  width: number;
  tabCount: number;
  progress: SharedValue<number>;
}) {
  const indicatorStyle = useAnimatedStyle(
    () => ({
      transform: [{ translateX: (width / tabCount) * (progress.get() + 0.5) - INDICATOR_WIDTH / 2 }],
    }),
    [width, tabCount, progress],
  );

  return <Animated.View pointerEvents="none" style={[styles.indicator, indicatorStyle]} />;
}

export function LandingFeedTabs<T extends string>({
  tabs,
  value,
  onPress,
  progress,
}: {
  tabs: { label: string; value: T }[];
  value: T;
  onPress: (value: T) => void;
  progress: SharedValue<number>;
}) {
  const [width, setWidth] = useState(0);

  return (
    <View onLayout={(event) => setWidth(event.nativeEvent.layout.width)} style={styles.container}>
      {tabs.map((tab, index) => {
        const selected = tab.value === value;
        const Icon = index === 0 ? IconCompass : IconUsersGroup;
        return (
          <Pressable
            key={tab.value}
            accessibilityRole="tab"
            accessibilityState={{ selected }}
            testID={`tab-${tab.value}`}
            onPress={() => onPress(tab.value)}
            style={({ pressed }) => [styles.tab, pressed && styles.pressed]}
          >
            <Icon size={18} strokeWidth={1.8} color={theme.colors[selected ? 'text.interactive' : 'text.secondary']} />
            <Text fontSize={15} fontWeight="600" color={selected ? 'text.interactive' : 'text.secondary'}>
              {tab.label}
            </Text>
          </Pressable>
        );
      })}
      {/* Initialize the animated style only after layout, so its cached initial position uses the measured width. */}
      {width > 0 ? <LandingFeedTabIndicator width={width} tabCount={tabs.length} progress={progress} /> : null}
    </View>
  );
}

export function LandingFeedPager<T extends string>({
  tabs,
  value,
  onChange,
  scenes,
  showTabs,
  swipeEnabled,
}: {
  tabs: { label: string; value: T }[];
  value: T;
  onChange: (value: T) => void;
  scenes: ReactNode[];
  showTabs: boolean;
  swipeEnabled: boolean;
}) {
  const [width, setWidth] = useState(0);
  const pagerRef = useRef<ScrollView>(null);
  const progress = useSharedValue(0);
  const reducedMotion = useReducedMotion();
  const selectedIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.value === value),
  );
  useEffect(() => {
    pagerRef.current?.scrollTo({ x: selectedIndex * width, animated: !reducedMotion });
  }, [reducedMotion, selectedIndex, width]);
  const handleScroll = useAnimatedScrollHandler((event) => {
    if (width > 0) progress.set(Math.max(0, Math.min(tabs.length - 1, event.contentOffset.x / width)));
  });

  const handlePageChange = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    if (width <= 0) return;
    const index = Math.max(0, Math.min(tabs.length - 1, Math.round(event.nativeEvent.contentOffset.x / width)));
    if (tabs[index]) onChange(tabs[index].value);
  };

  return (
    <View style={{ flex: 1 }} onLayout={(event) => setWidth(event.nativeEvent.layout.width)}>
      {showTabs && tabs.length > 1 ? (
        <LandingFeedTabs tabs={tabs} value={value} onPress={onChange} progress={progress} />
      ) : null}
      <Animated.ScrollView
        ref={pagerRef}
        horizontal
        pagingEnabled
        decelerationRate="fast"
        removeClippedSubviews={false}
        directionalLockEnabled
        nestedScrollEnabled
        bounces={false}
        scrollEnabled={swipeEnabled && tabs.length > 1}
        showsHorizontalScrollIndicator={false}
        scrollEventThrottle={16}
        onScroll={handleScroll}
        onScrollEndDrag={(event) => {
          if (width <= 0) return;
          const x = event.nativeEvent.contentOffset.x;
          // A partial page may still glide to another tab. Only commit an already settled page here.
          if (Math.abs(x - Math.round(x / width) * width) < 1) handlePageChange(event);
        }}
        onMomentumScrollEnd={handlePageChange}
        style={{ flex: 1 }}
        contentContainerStyle={{ flexGrow: 1 }}
      >
        {tabs.map((tab, index) => (
          <View
            key={tab.value}
            accessibilityElementsHidden={tab.value !== value}
            importantForAccessibility={tab.value === value ? 'auto' : 'no-hide-descendants'}
            style={{ width, height: '100%' }}
          >
            {scenes[index]}
          </View>
        ))}
      </Animated.ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    backgroundColor: theme.colors['surface.default'],
    borderBottomWidth: 1,
    borderBottomColor: theme.colors['border.secondary'],
  },
  tab: {
    flex: 1,
    minHeight: 48,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 7,
    paddingHorizontal: 8,
    paddingVertical: 12,
  },
  pressed: {
    opacity: 0.55,
  },
  indicator: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    width: INDICATOR_WIDTH,
    height: 3,
    borderRadius: 2,
    backgroundColor: theme.colors['text.interactive'],
  },
});
